import * as React from "react";
import { CloudArrowUp, CloudCheck, Pencil } from "phosphor-react";
import { Trash } from "phosphor-react";
import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { ImageListSortableItem } from "./ImageListSortableItem";
import api from "../../api/api";
import { getReadableSize } from "../Modals/CreateDeal/CreateEditUsedDealModal";

export default function StandardImageList({ images, setImages }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require the mouse to move by x pixels before activating, allowing onClick events to work
      activationConstraint: {
        distance: 2,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (!images || images?.length === 0)
    return (
      <div style={{display: "flex", height: "100%", alignItems: "center", justifyContent: "center"}}>
        No images loaded yet...
      </div>
    );

  // Remove image by id
  const handleRemove = async (id, imageID) => {
    // Handle backend
    if(imageID) {
      await api.deleteUsedDealImage(imageID);
    }

    // Handle local
    setImages((items) => {
      console.log('items', items);
      console.log('id', id);
      const newImages = items.filter(item => item.id !== id);
      console.log('newImages', newImages);
      
      return newImages.map((item, index) => {
          // Only mark as modified if it's a backend image and its order changed
          if (item.originalImageOrder !== undefined) {
            return {
              ...item,
              modified: item.originalImageOrder !== ++index
            };
          }
          // For local images, keep modified as is
          return item;
        });
    });
  };

  const handleEdit = async (e, id) => {
    const newImages = images.map(async (image) => {
      if(image.id === id) {
        const img = e.target.files[0];
        const buffer = await img.arrayBuffer();
        const byteArray = new Int8Array(buffer);
        const name = img.name;
        const ext = name.substring(name.lastIndexOf(".")).toLowerCase();

        const src = URL.createObjectURL(new Blob([buffer]));

        const size = getReadableSize(img.size);
        return {
          ...image,
          src: src,
          isLocal: true,
          modified: true, // assume new local images are modified
          fileName: name,
          fileExt: ext,
          byteArray: byteArray,
          size: size,
          tooLarge: (img.size / 1000) > 5000,
        }
      }
      return image;
    });
    const updatedImages = await Promise.all(newImages);
    setImages(updatedImages);
  }

  function handleDragEnd(event) {
    const {active, over} = event;

    if (active.id !== over.id) {
      setImages((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);

        return newOrder.map((item, index) => {
          // Only mark as modified if it's a backend image and its order changed
          if (item.originalImageOrder !== undefined) {
            return {
              ...item,
              modified: item.originalImageOrder !== ++index
            };
          }
          // For local images, keep modified as is
          return item;
        });
      });
    }
  }

  console.log('images', images);

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext 
        items={images}
        strategy={rectSortingStrategy}
      >
        {/* responsive wrapper for image items */}
        <div style={{display: 'grid', gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: '8px'}}>
          {images?.map((item, index) => (
            <ImageListSortableItem key={item.id} id={item.id}>
              <div
                key={item?.id}
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  borderRadius: '4px',
                  background: '#fff',
                  display: 'block',
                  alignItems: 'center',
                  transition: 'box-shadow 0.2s',
                  position: 'relative',
                  overflow: "clip",
                  flex: '0 0 120px', // fixed base width; wrapping will occur instead of shrinking
                  width: '100%',
                  height: "auto",
                }}
              >
                <img
                  src={item?.src}
                  alt={item?.id || 'Image'}
                  loading="lazy"
                  style={{
                    width: '100%',
                                        objectFit: 'cover',
                    background: '#f4f4f4',
                  }}
                />
                <div 
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    display: "flex",
                    gap: "8px"
                }}>
                  {/* <label
                    style={{
                      background: "white",
                      border: "none",
                      borderRadius: "50%",
                      padding: "4px",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.20)",
                      width: "28px",
                      height: "28px",
                      zIndex: 2,
                    }}
                    title="Change image"
                  >
                    <input type="file" hidden={true} onChange={(e) => handleEdit(e, item.id)} multiple={false} />
                    <Pencil color="gray" size={20} />
                  </label> */}
                  <button
                    onClick={() => handleRemove(item.id, item.imageID)}
                    style={{
                      background: "white",
                      border: "none",
                      borderRadius: "50%",
                      padding: "4px",
                      cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.20)",
                      width: "28px",
                      height: "28px",
                      zIndex: 2,
                    }}
                    title="Remove image"
                  >
                    <Trash color="#d32f2f" size={20} />
                  </button>
                </div>
                {item?.tooLarge && (
                  <div style={{position: "absolute", inset: "0", height: "120px", backgroundColor: "rgba(0, 0, 0, 0.7)", display: "flex", flexDirection: "column", gap: "8px", justifyContent: "center", alignItems: "center", zIndex: 1}}>
                    <p style={{color: "red", fontWeight: "bold", fontSize: "small"}}>File too large!</p>
                    <a href="https://www.picssizer.com/image-compressor" target="_blank" rel="noreferrer" style={{color: "white", fontWeight: "bold"}}>Compress image</a>
                  </div>
                )}
                <div style={{ width: '100%', fontSize: '14px', color: '#333', padding: "4px 12px" }}>
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: "4px"}}>
                    <div style={{padding: "0.5px 4px", borderRadius: "50px", backgroundColor: "#ededed", height: "min-content"}}>{++index}</div>
                    <div style={{padding: "0.5px 4px", borderRadius: "50px", color: `${(item.byteArray?.byteLength / 1000) > 5000 ? "red" : (item.byteArray?.byteLength / 1000) > 150 ? "orange" : "green"}`, height: "min-content"}}>{item.size}</div>
                    <div>
                      {item?.tooLarge ? <CloudX /> : (item?.isLocal || item?.modified) ? <CloudArrowUp color="blue" size={24} /> : <CloudCheck color="green" size={24} />}
                    </div>
                  </div>
                </div>
              </div>
            </ImageListSortableItem>
          ))}
          </div>
      </SortableContext>
    </DndContext>
  );
}

function CloudX() {
  return (<svg xmlns="http://www.w3.org/2000/svg" width="1.8em" height="1.8em" viewBox="0 0 256 256"><path fill="red" d="M160 40a88.09 88.09 0 0 0-78.71 48.67A64 64 0 1 0 72 216h88a88 88 0 0 0 0-176m0 160H72a48 48 0 0 1 0-96c1.1 0 2.2 0 3.29.11A88 88 0 0 0 72 128a8 8 0 0 0 16 0a72 72 0 1 1 72 72m29.66-82.34L171.31 136l18.35 18.34a8 8 0 0 1-11.32 11.32L160 147.31l-18.34 18.35a8 8 0 0 1-11.32-11.32L148.69 136l-18.35-18.34a8 8 0 0 1 11.32-11.32L160 124.69l18.34-18.35a8 8 0 0 1 11.32 11.32"/></svg>)
}